import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { IconButton, TopBar } from '@aibuild/ui';
import { useTheme } from '@contexts/useTheme';
import useSnackbar from '@hooks/useSnackbar';
import FeedbackWidget from '@components/FeedbackWidget/FeedbackWidget';
import UserProfile from '@components/UserProfile';
import MobileMenuButton from '@components/MobileMenuButton';
import type {
  CreateProcessRequest,
  ProcessListItemDto,
  ProcessListSort,
  ProcessListStatusFilter,
} from '@api/processes';
import { useProcesses } from './hooks/useProcesses';
import { useProcessActions } from './hooks/useProcessActions';
import { useProcessesCounts } from './hooks/useProcessesCounts';
import { useInfiniteScrollSentinel } from './hooks/useInfiniteScrollSentinel';
import DeleteProcessDialog from './components/DeleteProcessDialog';
import NewProcessDialog from './components/NewProcessDialog';
import ProcessesEmpty from './components/ProcessesEmpty';
import ProcessesError from './components/ProcessesError';
import ProcessesLoading from './components/ProcessesLoading';
import ProcessesList from './components/ProcessesList';
import ProcessesToolbar from './components/ProcessesToolbar';
import {
  ScrollFooter,
  ScrollFooterSpinner,
} from './components/ProcessesStates.styled';
import { PageBody, PageRoot } from './ProcessesPage.styled';

const HELP_TEXT =
  'Automated, repeatable pipelines you can run on inputs to produce builds, exports, or analyses.';

const ProcessesPage: React.FC = () => {
  const { themeName, setThemePreference } = useTheme();
  const { showSuccess, showError } = useSnackbar();
  const navigate = useNavigate();
  const isDark = themeName === 'dark';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ProcessListStatusFilter>('all');
  const [sort, setSort] = useState<ProcessListSort>('newest');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProcessListItemDto | null>(
    null,
  );

  const query = useProcesses({ status, search, sort, size: 20 });
  const counts = useProcessesCounts(search);
  const actions = useProcessActions();

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useInfiniteScrollSentinel(sentinelRef, {
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    rootRef: scrollRef,
  });

  const processes = useMemo<ProcessListItemDto[]>(() => {
    if (!query.data) return [];
    const seen = new Set<string>();
    return query.data.pages
      .flatMap((p) => p.processes)
      .filter((p) => {
        if (seen.has(p.processId)) return false;
        seen.add(p.processId);
        return true;
      });
  }, [query.data]);

  const hasActiveFilters = status !== 'all' || search.trim().length > 0;

  const handleRetry = useCallback(() => {
    // Refetch is fire-and-forget — failures surface via `query.error` which
    // re-renders the error pane. `.catch` instead of `void` keeps Sonar
    // happy without changing semantics.
    query.refetch().catch(() => undefined);
  }, [query]);

  const handleToggleTheme = useCallback(() => {
    setThemePreference(isDark ? 'light' : 'dark');
  }, [isDark, setThemePreference]);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStatus('all');
  }, []);

  const handleCreate = useCallback(
    async (req: CreateProcessRequest) => {
      try {
        const created = await actions.create(req);
        setDialogOpen(false);
        navigate(`/processes/${encodeURIComponent(created.processId)}?layout=auto`);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to create process.';
        showError(message);
      }
    },
    [actions, navigate, showError],
  );

  const handleOpen = useCallback(
    (p: ProcessListItemDto) => {
      navigate(`/processes/${encodeURIComponent(p.processId)}`);
    },
    [navigate],
  );

  const handleRun = useCallback(
    (p: ProcessListItemDto) => {
      navigate(
        `/processes/${encodeURIComponent(p.processId)}?action=run`,
      );
    },
    [navigate],
  );

  const handleRename = useCallback(
    async (p: ProcessListItemDto, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === p.name) return;
      try {
        await actions.rename({ processId: p.processId, name: trimmed });
        showSuccess(`Renamed to “${trimmed}”.`);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to rename process.';
        showError(message);
      }
    },
    [actions, showError, showSuccess],
  );

  const handleDuplicate = useCallback(
    async (p: ProcessListItemDto) => {
      try {
        const dup = await actions.duplicate({ processId: p.processId });
        showSuccess(`Duplicated as “${dup.name}”.`);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to duplicate process.';
        showError(message);
      }
    },
    [actions, showError, showSuccess],
  );

  const handleSendCopy = useCallback(
    (p: ProcessListItemDto) => {
      // Send-copy needs a teammate-picker dialog (Phase 1 spec). For PR2 we
      // surface a placeholder rather than wire half a flow.
      showSuccess(`Send-copy picker for “${p.name}” is coming next.`);
    },
    [showSuccess],
  );

  const handleDelete = useCallback(
    async (p: ProcessListItemDto) => {
      try {
        await actions.remove(p.processId);
        setDeleteTarget(null);
        showSuccess(`Deleted "${p.name}".`);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to delete process.';
        showError(message);
      }
    },
    [actions, showError, showSuccess],
  );

  const isInitialLoading = query.isLoading;
  const isError = query.isError;
  const error = query.error ?? new Error('Unknown error');
  const isEmpty = !isInitialLoading && !isError && processes.length === 0;
  const hasMore = query.hasNextPage ?? false;
  const isLoadingMore = query.isFetchingNextPage;

  // Three-state machine for the infinite-scroll footer driving the
  // `data-state` attribute. Lifted out of JSX so the markup avoids a nested
  // ternary (Sonar code-smell).
  let scrollFooterState: 'loading' | 'idle' | 'end';
  if (isLoadingMore) scrollFooterState = 'loading';
  else if (hasMore) scrollFooterState = 'idle';
  else scrollFooterState = 'end';

  let body: React.ReactNode;
  if (isInitialLoading) {
    body = <ProcessesLoading />;
  } else if (isError) {
    body = <ProcessesError error={error} onRetry={handleRetry} />;
  } else if (isEmpty) {
    body = (
      <ProcessesEmpty
        hasActiveFilters={hasActiveFilters}
        onCreate={() => setDialogOpen(true)}
        onClearFilters={handleClearFilters}
      />
    );
  } else {
    body = (
      <>
        <ProcessesList
          processes={processes}
          onOpen={handleOpen}
          onRun={handleRun}
          onRename={handleRename}
          onDuplicate={handleDuplicate}
          onSendCopy={handleSendCopy}
          onDelete={setDeleteTarget}
          isMutating={actions.isAnyMutationPending}
        />
        <ScrollFooter
          ref={sentinelRef}
          data-testid="processes-page__scroll-footer"
          data-state={scrollFooterState}
          aria-live="polite"
        >
          {isLoadingMore && (
            <>
              <ScrollFooterSpinner aria-hidden="true" />
              Loading more processes…
            </>
          )}
        </ScrollFooter>
      </>
    );
  }

  return (
    <PageRoot data-testid="processes-page">
      <TopBar
        title="Processes"
        helpText={HELP_TEXT}
        leading={<MobileMenuButton />}
        actions={
          <>
            <FeedbackWidget
              mode="inline"
              dataTestId="processes-page__feedback"
            />
            <IconButton
              ariaLabel={
                isDark ? 'Switch to light theme' : 'Switch to dark theme'
              }
              icon={
                isDark ? <MdLightMode size={20} /> : <MdDarkMode size={20} />
              }
              onClick={handleToggleTheme}
              dataTestId="processes-page__theme-toggle"
            />
            <UserProfile />
          </>
        }
      />
      <PageBody ref={scrollRef} data-testid="processes-page__scroll">
        <ProcessesToolbar
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          sort={sort}
          onSortChange={setSort}
          onCreate={() => setDialogOpen(true)}
          isCreating={actions.isCreating}
          counts={counts}
        />
        {body}
      </PageBody>
      <NewProcessDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreate={handleCreate}
        isCreating={actions.isCreating}
      />
      <DeleteProcessDialog
        process={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={actions.isRemoving}
      />
    </PageRoot>
  );
};

export default ProcessesPage;
